import { parseQuery } from '../shared/url'

const PREFIX = RUNTIME_KEY || 'w'

// see https://github.com/microsoft/TypeScript/issues/17867
export interface LaunchParams {
  id?: number

  [key: string]: string | number
}

export type LaunchEventListener = (params: LaunchParams) => void

export function parseQueryParams(query: string): LaunchParams {
  if (query.match(/^\d+$/)) {
    query = `id=${query}`
  }

  const { id, ...rest } = parseQuery(query)
  const intId = parseInt(id, 10)

  return {
    ...(!isNaN(intId) ? { id: intId } : {}),
    ...rest,
  }
}

export function parseHrefParams(href: string): LaunchParams {
  // #getsitecontrol?id=30&price=25
  const match = href.match(new RegExp(`^#${PREFIX}\\?(.+)`, 'i'))
  return match ? parseQueryParams(match[1]) : {}
}

export function parseElementParams(el: Element): LaunchParams {
  if (!el) {
    return null
  }

  const parsers: Array<[string, (n: string) => LaunchParams]> = [
    ['href', parseHrefParams],
    [`data-${PREFIX}`, parseQueryParams],
  ]

  return parsers.reduce<LaunchParams>((result, [name, parser]) => {
    const value = el.getAttribute(name)
    return {
      ...result,
      ...(value && parser(value)),
    }
  }, {})
}

function isParamsNotEmpty(params: LaunchParams): boolean {
  return Object.keys(params || {}).length > 0
}

function listenToLaunchEvent(callback: LaunchEventListener): () => void {
  const lookupElementParams = (el: Element, lvl = 1): LaunchParams | undefined => {
    if (lvl > 3) {
      return
    }

    const params = parseElementParams(el)
    if (!isParamsNotEmpty(params)) {
      return el.parentElement && lookupElementParams(el.parentElement, lvl + 1)
    }
    return params
  }

  const handleClick = (e: Event) => {
    const el = e.target
    if (!(el instanceof Element)) {
      return
    }

    const params = lookupElementParams(el)

    if (isParamsNotEmpty(params)) {
      callback(params)
      if (params.id) {
        e.preventDefault()
      }
    }
  }

  document.addEventListener('click', handleClick, false)

  return () => {
    document.removeEventListener('click', handleClick)
  }
}

export default class Launcher {
  private _cleanup: () => void | null
  private _eventListeners: LaunchEventListener[] = []

  public addEventListener(listener: LaunchEventListener): () => void {
    this._eventListeners.push(listener)
    if (!this._cleanup) {
      this._cleanup = listenToLaunchEvent(params => this._dispatchEvent(params))
    }

    return () => {
      this._eventListeners = this._eventListeners.filter(l => l !== listener)
      if (this._eventListeners.length === 0 && this._cleanup) {
        this._cleanup()
        this._cleanup = null
      }
    }
  }

  public processHref(href: string): void {
    const params = href && parseHrefParams(href)
    if (isParamsNotEmpty(params)) {
      this._dispatchEvent(params)
    }
  }

  private _dispatchEvent(params: LaunchParams) {
    this._eventListeners.forEach(listener => listener(params))
  }
}
